import { useEffect, useState } from "react";
import axios from "axios";
import { getCookies } from "cookies-next";
import { config } from "../../data/config";
import { useTranslation } from 'react-i18next';
import BreadCrump from "../tools/breadcrump";
import HeaderOfPage from "../tools/headerOfPage";
import Swal from 'sweetalert2';

interface DaySlot {
    _id?: string;
    dayOfWeek: number;
    dayName: string;
    isActive: boolean;
    startTime: string;
    endTime: string;
}

const dayNames = [
    { en: 'Saturday', fa: 'شنبه' },
    { en: 'Sunday', fa: 'یکشنبه' },
    { en: 'Monday', fa: 'دوشنبه' },
    { en: 'Tuesday', fa: 'سه‌شنبه' },
    { en: 'Wednesday', fa: 'چهارشنبه' },
    { en: 'Thursday', fa: 'پنجشنبه' },
    { en: 'Friday', fa: 'جمعه' },
];

export default function CampaignTimeSlots() {
    const { t, i18n } = useTranslation();
    const [slots, setSlots] = useState<DaySlot[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSlots();
    }, []);

    const fetchSlots = async () => {
        try {
            setLoading(true);
            const cookies = getCookies();
            const response = await axios.get(`${config.url}/v1/dashboard/campaign/time-slots`, {
                headers: { Authorization: `Bearer ${cookies.token}` }
            });

            // Map backend data to our format - always show all 7 days
            const backendSlots = response.data.success ? response.data.data : [];
            const mappedSlots = dayNames.map((day, index) => {
                const existing = backendSlots.find((s: any) => s.dayOfWeek === index);
                return {
                    _id: existing?._id,
                    dayOfWeek: index,
                    dayName: i18n.language === 'fa' ? day.fa : day.en,
                    isActive: existing?.isActive ?? (index !== 6), // Friday off by default
                    startTime: existing?.startTime || '10:00',
                    endTime: existing?.endTime || '19:00'
                };
            });
            setSlots(mappedSlots);
        } catch (error) {
            console.error('Error fetching slots:', error);
            // Even on error, show default slots
            const defaultSlots = dayNames.map((day, index) => ({
                dayOfWeek: index,
                dayName: i18n.language === 'fa' ? day.fa : day.en,
                isActive: index !== 6, // Friday off by default
                startTime: '10:00',
                endTime: '19:00'
            }));
            setSlots(defaultSlots);
        } finally {
            setLoading(false);
        }
    };

    const updateSlot = async (dayOfWeek: number, updates: Partial<DaySlot>) => {
        try {
            const slot = slots.find(s => s.dayOfWeek === dayOfWeek);
            const cookies = getCookies();

            if (!slot?._id) {
                // Create new slot
                await axios.post(`${config.url}/v1/dashboard/campaign/time-slots`, {
                    dayOfWeek,
                    startTime: slot?.startTime || '10:00',
                    endTime: slot?.endTime || '19:00',
                    ...updates
                }, {
                    headers: { Authorization: `Bearer ${cookies.token}` }
                });
            } else {
                // Update existing slot
                await axios.put(`${config.url}/v1/dashboard/campaign/time-slots/${slot._id}`, updates, {
                    headers: { Authorization: `Bearer ${cookies.token}` }
                });
            }

            // Refetch to get updated _id
            await fetchSlots();
        } catch (error) {
            console.error('Error updating slot:', error);
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: t('Failed to update time slot')
            });
        }
    };

    const toggleDay = (dayOfWeek: number) => {
        const slot = slots.find(s => s.dayOfWeek === dayOfWeek);
        if (slot) {
            updateSlot(dayOfWeek, { isActive: !slot.isActive });
        }
    };

    const updateTime = (dayOfWeek: number, field: 'startTime' | 'endTime', value: string) => {
        updateSlot(dayOfWeek, { [field]: value });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <BreadCrump data={[
                { name: t('Dashboard'), url: '/dashboard' },
                { name: t('Campaign'), url: '/dashboard/Campaign/Requests' },
                { name: t('Time Slots'), url: '/dashboard/Campaign/TimeSlots' }
            ]} />

            <HeaderOfPage name={t('Configure Working Hours')} />

            {/* Info Box */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
                <h3 className="font-medium text-purple-800 mb-2">📅 {t('How it works')}</h3>
                <ul className="text-sm text-purple-700 space-y-1 list-disc list-inside">
                    <li>{t('Toggle days on/off to enable/disable booking for that day')}</li>
                    <li>{t('Set working hours for each day (start and end time)')}</li>
                    <li>{t('Users can book 30-minute slots within the working hours')}</li>
                    <li>{t('Calendar starts from 2 days in advance')}</li>
                </ul>
            </div>

            {/* Days List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white p-4">
                    <h2 className="text-lg font-medium">{t('Weekly Schedule')}</h2>
                </div>

                <div className="divide-y">
                    {slots.map((slot) => (
                        <div
                            key={slot.dayOfWeek}
                            className={`p-4 flex flex-wrap items-center justify-between gap-4 ${!slot.isActive ? 'bg-gray-50' : ''
                                }`}
                        >
                            {/* Day Name & Toggle */}
                            <div className="flex items-center gap-4 min-w-[200px]">
                                <button
                                    onClick={() => toggleDay(slot.dayOfWeek)}
                                    className={`relative w-14 h-7 rounded-full transition-colors ${slot.isActive ? 'bg-green-500' : 'bg-gray-300'
                                        }`}
                                >
                                    <span
                                        className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${slot.isActive ? 'right-1' : 'left-1'
                                            }`}
                                    />
                                </button>
                                <span className={`font-medium text-lg ${slot.isActive ? 'text-gray-900' : 'text-gray-400'
                                    }`}>
                                    {slot.dayName}
                                </span>
                            </div>

                            {/* Time Range */}
                            {slot.isActive && (
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-gray-600">{t('From')}:</label>
                                        <input
                                            type="time"
                                            value={slot.startTime}
                                            onChange={(e) => updateTime(slot.dayOfWeek, 'startTime', e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>
                                    <span className="text-gray-400">—</span>
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-gray-600">{t('To')}:</label>
                                        <input
                                            type="time"
                                            value={slot.endTime}
                                            onChange={(e) => updateTime(slot.dayOfWeek, 'endTime', e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Status Badge */}
                            <div className={`px-3 py-1 rounded-full text-sm font-medium ${slot.isActive
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                                }`}>
                                {slot.isActive ? t('Open') : t('Closed')}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
